import Image from 'next/image';
import React from 'react';
import { motion, useSpring } from 'framer-motion';

import one from '@/assets/images/office/1.png';
import two from '@/assets/images/office/2.png';
import three from '@/assets/images/office/3.png';
import four from '@/assets/images/office/4.png';
import five from '@/assets/images/office/5.png';
import six from '@/assets/images/office/6.png';

interface ImageGridProps {
  scrollY: number;
  disableParallax: boolean;
}

// Custom hook that creates a spring and updates it on scrollY change
const useParallax = (scrollY: number, factor: number) => {
  const springY = useSpring(0, {
    stiffness: 80,
    damping: 20,
    mass: 0.5,
  });
  React.useEffect(() => {
    springY.set(-scrollY * factor);
  }, [scrollY, factor, springY]);
  return springY;
};

export const ImageGrid: React.FC<ImageGridProps> = ({ scrollY, disableParallax }) => {
  // Always call the parallax hooks (unconditionally)
  const y1 = useParallax(scrollY, 0.2);
  const y2 = useParallax(scrollY, 0.3);
  const y3 = useParallax(scrollY, 0.5);
  const y4 = useParallax(scrollY, 0.3);
  const y5 = useParallax(scrollY, 0.45);
  const y6 = useParallax(scrollY, 0.6);
  const y7 = useParallax(scrollY, 0.4);

  if (disableParallax) {
    return (
      <div className='flex flex-col items-start justify-start sm:grid sm:grid-cols-2 md:grid-cols-3 gap-4'>
        {[two, three].map((src, idx) => (
          <div key={idx} className='rounded-[40px] flex items-start justify-start relative w-full'>
            <Image src={src} alt={`Office image ${idx + 1}`} width={400} height={400} className='rounded-[40px] object-cover' />
          </div>
        ))}
      </div>
    );
  } else {
    return (
      <div className='relative w-full h-screen overflow-hidden'>
        {/* Image 1 */}
        <motion.div style={{ y: y1 }} className='absolute top-20 left-20 rounded-[40px]'>
          <Image src={one} alt='Office image 1' width={164} height={247} className='object-cover rounded-[40px]' />
        </motion.div>
        {/* Image 2 */}
        <motion.div style={{ y: y2 }} className='absolute h-[408px] right-[94px] top-[54px] w-[271px] rounded-[40px]'>
          <Image src={two} alt='Office image 2' fill className='object-cover rounded-[40px]' />
        </motion.div>
        {/* Image 3 */}
        <motion.div style={{ y: y3 }} className='absolute h-[388px] left-[270px] top-[374px] rounded-[40px]'>
          <Image src={three} alt='Office image 3' width={258} height={388} className='object-cover rounded-[40px]' />
        </motion.div>
        {/* Image 4 */}
        <motion.div style={{ y: y4 }} className='absolute h-[247px] right-[365px] top-[639px] w-[165px] rounded-[40px]'>
          <Image src={four} alt='Office image 4' fill className='object-cover rounded-[40px]' />
        </motion.div>
        {/* Image 5 */}
        <motion.div style={{ y: y5 }} className='absolute max-xl:left-[75%] xl:left-[932px] top-[971px] h-[388px] w-[260px] rounded-[40px]'>
          <Image src={five} alt='Office image 5' fill className='object-cover rounded-[40px]' />
        </motion.div>
        {/* Image 6 */}
        <motion.div style={{ y: y6 }} className='absolute left-20 top-[895px] h-[173px] w-[260px] rounded-[40px]'>
          <Image src={six} alt='Office image 6' fill className='object-cover rounded-[40px]' />
        </motion.div>
        {/* Duplicate Image 3 */}
        <motion.div style={{ y: y7 }} className='absolute h-[388px] left-[540px] top-[1200px] rounded-[40px]'>
          <Image src={three} alt='Office image 3 duplicate' width={258} height={388} className='object-cover rounded-[40px]' />
        </motion.div>
      </div>
    );
  }
};

export default ImageGrid;